import { Fragment, useEffect, useState, type ReactNode } from 'react';
import { api, type CampAccountRow, type ClerkUserRow, type CommerceOrgRow, type IdentityRow } from '../api';
import { LoginAsCamp, useLoad } from '../lib';

/** Phone · email · shortened id — whatever this Clerk record actually carries. */
const userMeta = (u: ClerkUserRow) => [u.phone, u.email, `${u.clerkUserId.slice(0, 12)}…`].filter(Boolean).join(' · ');

// Find a signed-up user and map them to a camp (buyer/shadow) or grant ops; turn anyone's access off
// or back on. Commerce grants a Clerk USER ID picked from Clerk's own search — the live instance is
// phone-based, so a typed email matched nobody and the grant was refused. Turning access off keeps
// the grant (camp + role); the user's next request is refused (go/no-go #24).
export function AccessPanel({ canOps }: { canOps: boolean }) {
  const [userSearch, setUserSearch] = useState('');
  const [users, setUsers] = useState<ClerkUserRow[]>([]);
  const [picked, setPicked] = useState<ClerkUserRow | null>(null);
  const [searching, setSearching] = useState(false);
  const [role, setRole] = useState<'buyer' | 'ops' | 'shadow'>('buyer');
  const [orgId, setOrgId] = useState<number | null>(null);
  const [orgSearch, setOrgSearch] = useState('');
  const [orgs, setOrgs] = useState<CommerceOrgRow[]>([]);
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const { data, err, reload } = useLoad(() => api.commerceIdentities());

  useEffect(() => {
    if (role === 'ops') return; // buyer and shadow both need a camp
    const t = setTimeout(() => { api.commerceOrgs(orgSearch).then(setOrgs).catch(() => {}); }, 200);
    return () => clearTimeout(t);
  }, [orgSearch, role]);

  // Clerk user search — skipped once a user is picked, so the name written into the box is not re-queried.
  useEffect(() => {
    if (picked) return;
    const term = userSearch.trim();
    if (term.length < 2) { setUsers([]); setSearching(false); return; }
    setSearching(true);
    const t = setTimeout(() => {
      api.commerceClerkUsers(term).then(setUsers).catch((e) => { setUsers([]); setMsg(`⚠ ${(e as Error).message}`); }).finally(() => setSearching(false));
    }, 250);
    return () => clearTimeout(t);
  }, [userSearch, picked]);

  const pick = (u: ClerkUserRow) => { setPicked(u); setUserSearch(u.name ?? u.phone ?? u.email ?? u.clerkUserId); setUsers([]); };
  const clearPick = () => { setPicked(null); setUserSearch(''); setUsers([]); };

  async function submit() {
    if (!picked) return;
    setMsg(null);
    setBusy(true);
    try {
      await api.commerceProvision({ clerkUserId: picked.clerkUserId, role, orgId: role === 'ops' ? null : orgId });
      const camp = orgs.find((o) => o.id === orgId)?.name ?? 'camp';
      const who = picked.name ?? picked.phone ?? picked.email ?? picked.clerkUserId;
      setMsg(`✓ ${who} → ${role === 'ops' ? 'Ops' : role === 'shadow' ? `${camp} (shadow, read-only)` : camp}`);
      clearPick(); setOrgId(null); setOrgSearch('');
      reload();
    } catch (e) {
      setMsg(`⚠ ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  async function setAccess(r: IdentityRow, disable: boolean) {
    const who = r.display_name ?? r.email ?? r.clerk_user_id;
    if (disable && !window.confirm(`Turn off access for ${who}? Their next request to Tavezio is refused. You can turn it back on here.`)) return;
    setMsg(null);
    try {
      await api.commerceSetAccess(r.clerk_user_id, disable);
      setMsg(`✓ ${who}: access ${disable ? 'turned off' : 'turned back on'}`);
      reload();
    } catch (e) {
      setMsg(`⚠ ${(e as Error).message}`);
    }
  }
  const canSubmit = !!picked && (role === 'ops' || orgId != null) && !busy;

  return (
    <div className="stack">
      <h1>Access</h1>
      <p className="sub">Find a signed-up user, then map them to a camp (buyer) or grant ops — or turn someone's access off. Users must sign up first.</p>
      <section className="card">
        <div className="form-row">
          <label className="field"><span>User</span>
            <input className="search" placeholder="name, phone or email…" value={userSearch}
              onChange={(e) => { setUserSearch(e.target.value); if (picked) setPicked(null); }} />
            {picked ? (
              <span className="muted" style={{ fontSize: 'var(--fs-small)' }}>{userMeta(picked)} <button className="link" onClick={clearPick}>change</button></span>
            ) : userSearch.trim().length >= 2 && (
              <div className="typeahead">
                {users.map((u) => (
                  <button key={u.clerkUserId} className="ta-opt" onClick={() => pick(u)}>
                    {u.name ?? '(no name on record)'} <span className="muted">{userMeta(u)}</span>
                  </button>
                ))}
                {!searching && users.length === 0 && <div className="loading" style={{ padding: 8 }}>No match — they must sign up first.</div>}
                {searching && users.length === 0 && <div className="loading" style={{ padding: 8 }}>Searching…</div>}
              </div>
            )}
          </label>
          <label className="field"><span>Role</span>
            <select className="sel" value={role} onChange={(e) => setRole(e.target.value as 'buyer' | 'ops' | 'shadow')}>
              <option value="buyer">Buyer (camp)</option>
              <option value="shadow">Shadow (camp, read-only)</option>
              <option value="ops">Ops (all)</option>
            </select>
          </label>
          {role !== 'ops' && (
            <label className="field"><span>Camp</span>
              <input className="search" placeholder="search camps…" value={orgSearch} onChange={(e) => { setOrgSearch(e.target.value); setOrgId(null); }} />
              {orgSearch && !orgId && (
                <div className="typeahead">
                  {orgs.slice(0, 8).map((o) => <button key={o.id} className="ta-opt" onClick={() => { setOrgId(o.id); setOrgSearch(o.name); }}>{o.name}</button>)}
                  {orgs.length === 0 && <div className="loading" style={{ padding: 8 }}>No camps match.</div>}
                </div>
              )}
            </label>
          )}
          <button className="btn-sm" disabled={!canSubmit} onClick={submit}>{busy ? 'Saving…' : 'Grant access'}</button>
        </div>
        {msg && <p className={`msg ${msg.startsWith('⚠') ? 'bad' : 'good'}`} style={{ marginTop: 12 }}>{msg}</p>}
      </section>
      {err && <p className="msg bad">⚠ {err}</p>}
      <section className="card">
        <table>
          <thead><tr><th>Email</th><th>Name</th><th>Role</th><th>Camp</th><th>Ordering</th><th>Access</th><th></th></tr></thead>
          <tbody>
            {(data ?? []).map((r) => (
              <Fragment key={r.clerk_user_id}>
                <tr>
                  <td className="mono">{r.email ?? '—'}</td>
                  <td>{r.display_name ?? '—'}</td>
                  <td><span className={`pill${r.role === 'ops' ? ' role-ops' : ''}`}>{r.role === 'ops' ? 'Ops' : r.role === 'shadow' ? 'Shadow' : 'Buyer'}</span></td>
                  <td>{r.org_name ?? (r.role === 'ops' ? 'all camps' : '—')}{r.org_status === 0 && <span className="muted"> (camp inactive)</span>}</td>
                  <td>
                    <OrderingSummary r={r} edit={canOps && r.role === 'buyer' && r.org_id != null
                      ? <button className="link edit" onClick={() => setEditing(editing === r.clerk_user_id ? null : r.clerk_user_id)}>{editing === r.clerk_user_id ? 'close' : 'edit'}</button>
                      : null} />
                  </td>
                  <td>
                    {r.disabled_at ? (
                      <><span className="pill" style={{ color: 'var(--state-no)' }}>Off</span> <span className="muted">since {r.disabled_at.slice(0, 10)}{r.disabled_by ? ` by ${r.disabled_by}` : ''}</span>{' '}
                        <button className="link" onClick={() => setAccess(r, false)}>turn on</button></>
                    ) : (
                      <><span className="pill type">On</span> <button className="link" onClick={() => setAccess(r, true)}>turn off</button></>
                    )}
                  </td>
                  <td>{r.org_id != null && <LoginAsCamp orgId={r.org_id} name={r.org_name} />}</td>
                </tr>
                {editing === r.clerk_user_id && r.org_id != null && (
                  <tr><td colSpan={7}>
                    <OrderingEditor r={r} orgId={r.org_id} onSaved={(m) => { setMsg(m); setEditing(null); reload(); }} onError={(m) => setMsg(`⚠ ${m}`)} />
                  </td></tr>
                )}
              </Fragment>
            ))}
            {data && data.length === 0 && <tr><td colSpan={7} className="loading">No users provisioned yet.</td></tr>}
          </tbody>
        </table>
        {!data && !err && <p className="loading">Loading…</p>}
      </section>
    </div>
  );
}

// ── How a person orders (go/no-go #9b) ───────────────────────────────────────────────────────────────
// The same rule checkout applies (commerce shared/src/rules/permissions.ts orderingMember +
// approvesOrders): staff's choice, else the Magento account's role, else today's rule (orders directly).
type Role3 = { placeOrder: boolean | null; approvalNeeded: boolean | null; approves: boolean | null };

function ordering(role: Role3 | null, needsApproval: boolean | null, approvesOrders: boolean | null) {
  const loaded = role && role.placeOrder != null ? role : null;
  const how = needsApproval === true ? 'approval'
    : needsApproval === false || !loaded || loaded.placeOrder ? 'direct'
    : loaded.approvalNeeded ? 'approval' : 'none';
  return {
    how,
    text: how === 'approval' ? 'Needs approval' : how === 'direct' ? 'Orders directly' : 'Cannot order',
    approves: approvesOrders ?? loaded?.approves ?? false,
    source: needsApproval != null ? 'set by staff' : loaded ? 'from Magento role' : 'default rule',
  };
}

const roleOf = (r: IdentityRow): Role3 => ({ placeOrder: r.magento_place_order, approvalNeeded: r.magento_approval_needed, approves: r.magento_approves_orders });

function OrderingSummary({ r, edit }: { r: IdentityRow; edit: ReactNode }) {
  if (r.role !== 'buyer' || r.org_id == null) return <span className="muted">—</span>;
  const o = ordering(roleOf(r), r.needs_approval, r.approves_orders);
  return (
    <>
      {o.text}{o.approves && ' · approver'}
      <div className="muted small">{o.source}{r.app_user_name || r.app_user_email ? ` · as ${r.app_user_name ?? r.app_user_email}` : ''} {edit}</div>
    </>
  );
}

// null / true / false ⇄ the select's 'follow' / 'yes' / 'no'.
const toOpt = (v: boolean | null) => (v == null ? 'follow' : v ? 'yes' : 'no');
const fromOpt = (v: string) => (v === 'follow' ? null : v === 'yes');

function OrderingEditor({ r, orgId, onSaved, onError }: { r: IdentityRow; orgId: number; onSaved: (msg: string) => void; onError: (msg: string) => void }) {
  const accounts = useLoad(() => api.commerceCampAccounts(orgId), [orgId]);
  const [appUserId, setAppUserId] = useState<number | null>(r.app_user_id);
  const [needs, setNeeds] = useState(toOpt(r.needs_approval));
  const [approves, setApproves] = useState(toOpt(r.approves_orders));
  const [saving, setSaving] = useState(false);
  const acct: CampAccountRow | undefined = (accounts.data ?? []).find((a) => a.id === appUserId);
  const role: Role3 | null = acct ? { placeOrder: acct.place_order, approvalNeeded: acct.order_approval_needed, approves: acct.approve_orders } : null;
  const roleText = !acct ? 'No account tied — they order under the default rule (directly).'
    : acct.place_order == null ? 'This account’s Magento role has not been loaded yet.'
    : `Its Magento role: ${acct.place_order ? 'places orders' : acct.order_approval_needed ? 'orders need approval' : 'cannot order'}${acct.approve_orders ? ', approves the camp’s orders' : ''}.`;
  const result = ordering(role, fromOpt(needs), fromOpt(approves));
  const who = r.display_name ?? r.email ?? r.clerk_user_id;

  async function save() {
    setSaving(true);
    try {
      await api.commerceSetOrdering(r.clerk_user_id, { appUserId, needsApproval: fromOpt(needs), approvesOrders: fromOpt(approves) });
      onSaved(`✓ ${who}: ${result.text.toLowerCase()}${result.approves ? ', approves the camp’s orders' : ''}`);
    } catch (e) {
      onError((e as Error).message);
    } finally {
      setSaving(false);
    }
  }

  if (accounts.err) return <p className="msg bad">⚠ {accounts.err}</p>;
  return (
    <div className="stack">
      <div className="form-row">
        <label className="field"><span>Magento account</span>
          <select className="sel" value={appUserId ?? ''} disabled={!accounts.data} onChange={(e) => setAppUserId(e.target.value ? Number(e.target.value) : null)}>
            <option value="">{accounts.data ? '— none —' : 'Loading…'}</option>
            {(accounts.data ?? []).map((a) => (
              <option key={a.id} value={a.id}>
                {a.name ?? a.email ?? `account ${a.id}`}{a.email && a.name ? ` (${a.email})` : ''}{a.ordering_account ? ' — camp login' : ''}{a.is_active === false ? ' — inactive' : ''}
              </option>
            ))}
          </select>
        </label>
        <label className="field"><span>Orders need approval</span>
          <select className="sel" value={needs} onChange={(e) => setNeeds(e.target.value)}>
            <option value="follow">Follow Magento role</option>
            <option value="yes">Yes — send to approval</option>
            <option value="no">No — order directly</option>
          </select>
        </label>
        <label className="field"><span>Approves camp orders</span>
          <select className="sel" value={approves} onChange={(e) => setApproves(e.target.value)}>
            <option value="follow">Follow Magento role</option>
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </select>
        </label>
        <button className="btn-sm" disabled={saving || !accounts.data} onClick={save}>{saving ? 'Saving…' : 'Save'}</button>
      </div>
      <p className="muted small">{roleText} With these settings {who} <strong>{result.text.toLowerCase()}</strong>{result.approves ? ' and approves the camp’s orders' : ''}.</p>
    </div>
  );
}
